import {AxiosInstance} from "axios";
import {
    NotificationSequence, NotificationSequenceable,
    NotificationSequenceDetails,
    NotificationSequenceInstance, NotificationSequenceMessageInstance, NotificationSequenceMessageInstanceStatus,
    NotificationSequenceTargetedConsumer, UpcomingSequenceMessage, UpcomingSequenceMessageSummary
} from "@/notification-sequences/interfaces/NotificationSequence";
import {
    NotificationSequenceTargetingSearchParams
} from "@/notification-sequences/composables/useNotificationSequenceTargeting";
import {PaginatedData, PaginationRequest} from "@/core/interfaces/api";

export interface NotificationSequenceIndexQueryParams {
    organization_id?: number;
    laboratory_id?: number;
    program_id?: number;
    survey_id?: number;
    types?: ('program' | 'survey' | 'company' | 'laboratory')[];
    with?: 'templates'
}

export interface NotificationSequenceInstanceCreateRequest {
    consumer_key: string;
}

export interface NotificationSequenceInstanceIndexRequest {
    page?: number;
    limit?: number;
}

export interface NotificationSequenceMessageInstanceIndexRequest {
    status?: NotificationSequenceMessageInstanceStatus;
    updated_since?: string;
    page?: number;
    limit?: number;
}

type NotificationSequenceResult<T extends NotificationSequenceIndexQueryParams> =
    T extends { with: 'templates' } ? NotificationSequenceDetails[] : NotificationSequence[];

const NotificationSequenceResource = (apiClient: AxiosInstance, hdsClient: AxiosInstance) => {
    return {
        /**
         * Get all notification sequences.
         *
         * @param query
         */
        async index<T extends NotificationSequenceIndexQueryParams>(query: T = {} as T): Promise<NotificationSequenceResult<T>> {
            return apiClient.get('notification-sequences', {params: query})
                .then(response => response.data.data);
        },

        /**
         * Toggle enabled status for a notification sequence.
         *
         * @param sequenceId
         * @param enabled
         */
        async toggleEnabled(sequenceId: number, enabled: boolean): Promise<{ enabled: boolean }> {
            return apiClient.patch(`notification-sequences/${sequenceId}/toggle-enabled`, {enabled})
                .then(response => response.data.data);
        },

        /**
         * Read a notification sequence.
         * @param sequenceId
         */
        async read(sequenceId: number | string): Promise<NotificationSequenceDetails> {
            return apiClient.get(`notification-sequences/${sequenceId}`)
                .then(response => response.data.data);
        },

        /**
         * Get the sequence targeting.
         */
        async targeting(sequenceId: number | string, params: NotificationSequenceTargetingSearchParams = {}): Promise<NotificationSequenceTargetedConsumer[]> {
            return hdsClient.get(`notification-sequences/${sequenceId}/targeting`, {params})
                .then(response => response.data.data);
        },

        /**
         * Create a sequence instance.
         */
        async createInstance(sequenceId: number | string, body: NotificationSequenceInstanceCreateRequest): Promise<any> {
            return apiClient.post(`notification-sequences/${sequenceId}/instances`, body)
                .then(response => response.data.data);
        },

        /**
         * Get the sequence instance index.
         *
         * @param sequenceId
         * @param params
         */
        async instancesIndex(
            sequenceId: number | string,
            params: NotificationSequenceInstanceIndexRequest = {}
        ): Promise<PaginatedData<NotificationSequenceInstance[]>> {
            return hdsClient.get(`notification-sequences/${sequenceId}/instances`, {params})
                .then(response => response.data);
        },

        /**
         * Get the organization sequence message instances, filtered by status.
         *
         * @param params
         */
        async messageInstancesIndex(params: NotificationSequenceMessageInstanceIndexRequest = {}): Promise<PaginatedData<NotificationSequenceMessageInstance[]>> {
            return hdsClient.get('notification-sequence-message-instances', {params})
                .then(response => response.data);
        },

        /**
         * Get the organization upcoming sequence messages.
         *
         * @param params
         */
        async upcomingMessages(params: PaginationRequest = {}): Promise<PaginatedData<UpcomingSequenceMessage[]>> {
            return hdsClient.get('notification-sequence-messages/upcoming', {params})
                .then(response => response.data);
        },

        /**
         * Get the daily volume of upcoming sequence messages.
         *
         * @param days
         */
        async upcomingMessagesSummary(days?: number): Promise<UpcomingSequenceMessageSummary> {
            return hdsClient.get('notification-sequence-messages/upcoming-summary', {params: days ? {days} : {}})
                .then(response => response.data.data);
        },

        /**
         * Get available tags for notification sequences.
         */
        async tags(): Promise<NotificationSequenceable[]> {
            return apiClient.get('notification-sequences/tags')
                .then(response => response.data.data);
        }
    };
};

export default NotificationSequenceResource;
